import {
  Select,
  SelectOption,
  SelectVariant,
  ToolbarFilter,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { getVisitStatusLabel } from '@app/domain/visit/visit.helpers'
import { VisitStatusId } from '@app/domain/visit/visit.interface'

import { FilterCategories, FilterLabels } from './filters'

import type {
  SelectOptionObject,
  ToolbarChip} from '@patternfly/react-core';

interface Props {
  allowedStatuses?: string[]
  status: string[]
  showToolbarItem: boolean
  onStatusUpdated: (status: string[]) => void
}

const statuses = [
  {
    key: 'statusOrdered',
    value: VisitStatusId.Ordered,
    label: getVisitStatusLabel(VisitStatusId.Ordered),
  },
  {
    key: 'statusNotStarted',
    value: VisitStatusId.NotStarted,
    label: getVisitStatusLabel(VisitStatusId.NotStarted),
  },
  {
    key: 'statusInProgress',
    value: VisitStatusId.InProgress,
    label: getVisitStatusLabel(VisitStatusId.InProgress),
  },
  {
    key: 'statusCompleted',
    value: VisitStatusId.Completed,
    label: getVisitStatusLabel(VisitStatusId.Completed),
  },
  {
    key: 'statusCancelled',
    value: VisitStatusId.Cancelled,
    label: getVisitStatusLabel(VisitStatusId.Cancelled),
  },
]

export const StatusToolbarFilter = ({
  allowedStatuses: allowedStatus,
  status,
  onStatusUpdated,
  showToolbarItem,
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const statusMenuItems = useMemo(() => {
    if (allowedStatus && allowedStatus.length > 0) {
      return statuses
        .filter((it) => allowedStatus.includes(it.value))
        .map((status) => {
          return (
            <SelectOption
              key={status.key}
              value={status.value}
              label={status.label}
            />
          )
        })
    } else {
      return statuses.map((status) => {
        return (
          <SelectOption
            key={status.key}
            value={status.value}
            label={status.label}
          />
        )
      })
    }
  }, [allowedStatus])

  const onSelect = useCallback(
    (
      event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      if (
        event.target instanceof HTMLInputElement &&
        typeof selection === 'string'
      ) {
        const checked = event.target.checked
        const prevSelections = status
        const updatedStatus = checked
          ? [...prevSelections, selection]
          : prevSelections.filter((value) => value !== selection)

        onStatusUpdated(updatedStatus)
      }
    },
    [status, onStatusUpdated],
  )

  const onToggle = useCallback((isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
  }, [])

  const onDelete = useCallback(
    (_, value: string | ToolbarChip) => {
      const updatedStatus = status.filter((it) => it !== value)
      onStatusUpdated(updatedStatus)
    },
    [status, onStatusUpdated],
  )

  return (
    <ToolbarFilter
      chips={status ?? []}
      deleteChip={onDelete}
      categoryName={FilterLabels[FilterCategories.Status]}
      showToolbarItem={showToolbarItem}
    >
      <Select
        variant={SelectVariant.checkbox}
        aria-label={FilterLabels[FilterCategories.Status]}
        onToggle={onToggle}
        onSelect={onSelect}
        selections={status}
        isOpen={isDropdownOpen}
        placeholderText="Filter by status"
      >
        {statusMenuItems}
      </Select>
    </ToolbarFilter>
  )
}
